import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { type Locale } from '@/lib/i18n/config';

interface SEOHeadProps {
  title: string;
  description: string;
  path?: string; // ex: /blog/article-slug
  image?: string;
  type?: 'website' | 'article';
}

const baseUrl = 'https://aspexit.com';

// Langues avec du contenu traduit (pages statiques + articles)
const indexedLangs: Locale[] = ['fr', 'en', 'es', 'de', 'it'];

export function SEOHead({
  title,
  description,
  path = '',
  image,
  type = 'website',
}: SEOHeadProps) {
  const { locale } = useParams<{ locale: string }>();
  const currentLang = (locale || 'fr') as Locale;

  // Générer les hreflang uniquement pour les langues avec contenu réel
  const hreflangs = indexedLangs.map((lang) => ({
    rel: 'alternate',
    hreflang: lang,
    href: `${baseUrl}/${lang}${path}`,
  }));

  // x-default = français (langue par défaut)
  hreflangs.push({
    rel: 'alternate',
    hreflang: 'x-default',
    href: `${baseUrl}/fr${path}`,
  });

  // Canonical : toujours pointer vers fr ou en, jamais es/de/it
  const canonicalLang = indexedLangs.includes(currentLang) ? currentLang : 'fr';
  const canonicalUrl = `${baseUrl}/${canonicalLang}${path}`;
  const imageUrl = image || `${baseUrl}/og-image.jpg`;

  return (
    <Helmet>
      {/* Title & Description */}
      <title>{title} | Aspexit</title>
      <meta name="description" content={description} />

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang tags pour SEO multilingue */}
      {hreflangs.map((link) => (
        <link key={link.hreflang} {...link} />
      ))}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={currentLang} />
      <meta property="og:image" content={imageUrl} />

      {/* Open Graph alternate locales */}
      {indexedLangs
        .filter((lang) => lang !== currentLang)
        .map((lang) => (
          <meta key={lang} property="og:locale:alternate" content={lang} />
        ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:site" content="@CP_Leroux" />
      <meta name="twitter:creator" content="@CP_Leroux" />
    </Helmet>
  );
}
